import { useState, useEffect, useCallback, useMemo } from 'react'

/**
 * Uses draft state.
 * @param value
 * @param onCommit
 */
export function useDraftState<T>(
  value: T,
  onCommit: (value: T) => unknown,
): [T, (v: T) => unknown, () => unknown] {
  const [state, setState] = useState(value)
  useEffect(() => setState(value), [value])
  const commit = useCallback(() => onCommit(state), [state, onCommit])
  return [state, setState, commit]
}

/**
 * Simple toggle hook.
 */
export function useToggle(initial = false) {
  const [isEnabled, setEnabled] = useState(initial)
  return useMemo(
    () => ({
      isEnabled,
      set: setEnabled,
      toggle: () => {
        setEnabled((v) => !v)
      },
      enable: () => {
        setEnabled(true)
      },
      disable: () => {
        setEnabled(false)
      },
    }),
    [isEnabled],
  )
}
